import {
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconTable } from '@tabler/icons-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGruppen, usePersonalbedarf } from '@/api/useBelegung';
import { usePersonal } from '@/api/usePersonal';
import { usePlan, useUpdatePlan } from '@/api/usePlaene';
import {
  fachkraftebedarfMin,
  fachkraftebedarfSoll,
  fachkraftestandIst as berechneFachkraftestandIst,
} from '@/lib/fachkraefte';
import { FZM_CAP_RATIO } from '@/lib/fzm';
import { PersonalbedarfJahresuebersicht } from './PersonalbedarfJahresuebersicht';

const MONATE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { LoadingState } from '@/components/LoadingState';
import type { UUID } from '@/api/types';
import { IntegrationKinderZusammenfassung } from './IntegrationKinderFeld';
import { describeApiError } from './utils';

interface PersonalbedarfTabProps {
  planId: UUID;
  disabled: boolean;
  onMutated: () => void;
}

/**
 * Eigener Reiter "Personalbedarf" (rechts von "Personalkosten").
 *
 * Enthaelt die beiden Karten, die zuvor im Belegung-Reiter unter dem
 * Themenblock "Personalbedarf" standen: den Mindestpersonalbedarf nach
 * HKJGB (inkl. mpz-Feld; die Integrationskinder selbst werden im
 * Belegung-Reiter unter "Inklusion" gepflegt) und die
 * Gegenueberstellung "Stand Fachkraeftebedarf" (Soll/Min/Ist/Differenz).
 *
 * Der graue Themenblock-Rahmen der frueheren Fassung entfaellt hier: der
 * Reiter selbst ist die Gruppierung, eine zusaetzliche Ueberschrift
 * "PERSONALBEDARF" innerhalb eines gleichnamigen Reiters waere doppelt
 * gemoppelt.
 *
 * Die Uebersetzungsschluessel bleiben bewusst im Namensraum "belegung"
 * (``belegung:section.mindestbedarf.*`` / ``belegung:section.fachkraefte.*``).
 * Ein eigener Namensraum haette rein mechanische Umbenennungen ohne
 * fachlichen Gewinn bedeutet; die Schluessel beschreiben weiterhin
 * dieselben fachlichen Groessen.
 */
export function PersonalbedarfTab({ planId, disabled, onMutated }: PersonalbedarfTabProps) {
  const { t } = useTranslation(['belegung', 'common', 'errors']);

  const gruppen = useGruppen(planId);
  const personalbedarf = usePersonalbedarf(planId);
  const personalbedarfKapazitaet = usePersonalbedarf(planId, 'betriebserlaubnis');
  const personal = usePersonal(planId);
  const plan = usePlan(planId);
  const updatePlan = useUpdatePlan();

  /**
   * Betrachtungszeitraum der Fachkraefte-Gegenueberstellung.
   *
   * ``null`` = ganzjaehrig, also der Durchschnitt ueber zwoelf Monate —
   * so stand die Tabelle bisher da und so bleibt sie vorbelegt. Mit einem
   * Monat zeigt sie stattdessen genau diesen Monat. Der Unterschied ist
   * nicht kosmetisch: Eine Kita, die im Jahresmittel genug Personal hat,
   * kann im September trotzdem zu wenig haben, weil dann die meisten
   * Kinder da sind und eine Stelle erst im Januar beginnt.
   */
  const [bedarfMonat, setBedarfMonat] = useState<number | null>(null);

  /**
   * Grundlage der Gegenueberstellung: die geplante Belegung oder die
   * Betriebserlaubnis.
   *
   * Die Betriebserlaubnis beantwortet eine andere Frage — wie viel Personal
   * braeuchten wir, wenn das Haus voll waere. Sie kennt keinen
   * Jahresverlauf, deshalb ist die Monatswahl daneben gegenstandslos und
   * wird mit ihr zusammen in einer Liste gefuehrt statt in zweien.
   */
  const [bedarfBasis, setBedarfBasis] = useState<'belegung' | 'betriebserlaubnis'>(
    'belegung',
  );

  /** Die Abfrage, die zum gewaehlten Bezug gehoert. */
  const bedarfQuelle =
    bedarfBasis === 'betriebserlaubnis' ? personalbedarfKapazitaet : personalbedarf;

  /** Monatswerte, zugeschnitten auf den gewaehlten Zeitraum. */
  const bedarfWerte = React.useMemo(() => {
    if (bedarfBasis === 'betriebserlaubnis') {
      // Alle zwoelf Monate sind gleich; der Durchschnitt ueber sie ist
      // derselbe Wert. Ein Zuschnitt auf einen Monat waere folgenlos.
      return personalbedarfKapazitaet.data?.monatswerte ?? [];
    }
    const alle = personalbedarf.data?.monatswerte ?? [];
    return bedarfMonat == null ? alle : alle.filter((m) => m.monat === bedarfMonat);
  }, [personalbedarf.data, personalbedarfKapazitaet.data, bedarfMonat, bedarfBasis]);

  // FzM-Cap: 30 % vom Mindestpersonalbedarf nach HKJGB. FzM-Stellen
  // tragen nur ihren zulaessigen Anteil zum Ist bei. Der Cap folgt dem
  // gewaehlten Zeitraum — sonst verglichen wir Monats-Ist gegen
  // Jahres-Cap.
  const fzmLimit = React.useMemo(() => {
    const mpzFactor = plan.data?.mpz_anteil != null ? Number(plan.data.mpz_anteil) : null;
    return fachkraftebedarfMin(bedarfWerte, mpzFactor) * FZM_CAP_RATIO;
  }, [bedarfWerte, plan.data?.mpz_anteil]);

  // Vorhandene Fachkraft-Wochenstunden (Helper in lib/fachkraefte.ts, dort
  // dokumentiert + unit-getestet).
  const fachkraftestandIst = React.useMemo(
    () =>
      personal.data
        ? berechneFachkraftestandIst(
            personal.data,
            fzmLimit,
            // Ohne Foerderjahr koennte der Helfer den Ausbildungsverlauf
            // nicht auswerten — der Anteil einer PivA-Stelle springt mit
            // dem Ausbildungsjahr mitten im Jahr.
            plan.data?.foerderjahr,
            // Die Betriebserlaubnis kennt keinen Monat — dort gilt der
            // Jahresblick auf das vorhandene Personal.
            bedarfBasis === 'betriebserlaubnis' ? undefined : bedarfMonat ?? undefined,
          )
        : 0,
    [personal.data, fzmLimit, plan.data?.foerderjahr, bedarfMonat, bedarfBasis],
  );

  const [bedarfTableOpen, setBedarfTableOpen] = useState(false);
  // mpz_anteil als Prozent (0..200). null = Default 15%.
  const [mpzPct, setMpzPct] = useState<number | ''>('');
  // Auf plan.data.mpz_anteil syncen.
  React.useEffect(() => {
    const stored = plan.data?.mpz_anteil;
    setMpzPct(stored != null ? Number((stored * 100).toFixed(2)) : '');
  }, [plan.data?.mpz_anteil]);

  const persistMpz = (raw: number | '') => {
    if (!planId) return;
    const factor = raw === '' || Number.isNaN(raw) ? null : Number((raw / 100).toFixed(4));
    const unchanged = plan.data && plan.data.mpz_anteil === factor;
    if (unchanged) {
      // Snapshot trotzdem neu berechnen lassen, falls er aus einer
      // frueheren Engine-Version stammt.
      onMutated();
      return;
    }
    void updatePlan
      .mutateAsync({ id: planId, patch: { mpz_anteil: factor } })
      .then(() => onMutated())
      .catch((err) => {
        notifications.show({
          color: 'red',
          title: t('errors:generic'),
          message: describeApiError(err) || t('errors:generic'),
        });
      });
  };

  if (gruppen.isLoading) return <LoadingState height={200} />;
  if (gruppen.isError) return <ErrorState error={gruppen.error} />;
  // Ohne Gruppen gibt es keinen Bedarf zu zeigen. Statt eines leeren
  // Reiters der Hinweis, wo die Gruppen gepflegt werden — sonst wirkt der
  // Reiter kaputt, obwohl nur die Vorbedingung fehlt.
  if (!gruppen.data || gruppen.data.length === 0) {
    return <EmptyState description={t('belegung:section.personalbedarf.keineGruppen')} />;
  }

  return (
    <Stack>
      {/* ─── Mindestpersonalbedarf-Section ─── */}
      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
          <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
            <Title order={5}>{t('belegung:section.mindestbedarf.title')}</Title>
            <Text size="xs" c="dimmed">
              {t('belegung:section.mindestbedarf.subtitle')}
            </Text>
          </Stack>
          <Stack gap={2} style={{ minWidth: 180 }}>
            <NumberInput
              label={t('belegung:section.mindestbedarf.mpzLabel')}
              description={t('belegung:section.mindestbedarf.mpzHint')}
              placeholder="z.B. 15%"
              min={0}
              max={200}
              step={1}
              decimalScale={2}
              suffix="%"
              value={mpzPct}
              onChange={(v) => setMpzPct(typeof v === 'number' ? v : '')}
              onBlur={() => persistMpz(mpzPct)}
              disabled={disabled || updatePlan.isPending}
              size="xs"
              aria-label={t('belegung:section.mindestbedarf.mpzLabel')}
            />
          </Stack>
          <IntegrationKinderZusammenfassung planId={planId} />
          <Group gap="xs" align="center" wrap="nowrap">
            <Stack gap={2} align="flex-end">
              <Text size="xl" fw={600} style={{ whiteSpace: 'nowrap' }}>
                {bedarfQuelle.data
                  ? `${bedarfQuelle.data.max_h_woche.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })} h/W`
                  : '—'}
              </Text>
              <Text size="xs" c="dimmed">
                {t('belegung:section.mindestbedarf.maxHint')}
              </Text>
            </Stack>
            {/* Die Jahresuebersicht zeigt den Verlauf ueber zwoelf
                Monate. Bei der Betriebserlaubnis gibt es keinen — zwoelf
                gleiche Spalten waeren eine leere Geste. */}
            {bedarfBasis === 'belegung' &&
            personalbedarf.data &&
            personalbedarf.data.monatswerte.length > 0 ? (
              <Button
                variant="light"
                size="xs"
                leftSection={<IconTable size={16} />}
                onClick={() => setBedarfTableOpen(true)}
              >
                {t('belegung:section.mindestbedarf.jahresuebersicht')}
              </Button>
            ) : null}
          </Group>
        </Group>
      </Paper>

      {personalbedarf.data ? (
        <PersonalbedarfJahresuebersicht
          opened={bedarfTableOpen}
          onClose={() => setBedarfTableOpen(false)}
          monatswerte={personalbedarf.data.monatswerte}
          foerderjahr={plan.data?.foerderjahr ?? new Date().getFullYear()}
        />
      ) : null}

      {/* ─── Stand Fachkräftebedarf (kompakte 4-Zeilen-Tabelle) ─── */}
      {(() => {
        const mpzFactor = plan.data?.mpz_anteil != null ? Number(plan.data.mpz_anteil) : null;
        const soll = fachkraftebedarfSoll(bedarfWerte);
        const min = fachkraftebedarfMin(bedarfWerte, mpzFactor);
        const ist = fachkraftestandIst;
        const diff = ist - soll;
        const fmt = (n: number) =>
          n.toLocaleString('de-DE', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
        return (
          <Paper withBorder p="md">
            <Group justify="space-between" align="flex-end" wrap="nowrap" mb="xs">
              <Title order={5}>{t('belegung:section.fachkraefte.title')}</Title>
              <Group gap="sm" wrap="nowrap" align="flex-end">
                <Select
                  label={t('belegung:section.fachkraefte.bezug')}
                  data={[
                    {
                      value: 'belegung',
                      label: t('belegung:section.fachkraefte.bezugBelegung'),
                    },
                    {
                      value: 'betriebserlaubnis',
                      label: t('belegung:section.fachkraefte.betriebserlaubnis'),
                    },
                  ]}
                  value={bedarfBasis}
                  onChange={(v) =>
                    setBedarfBasis(v === 'betriebserlaubnis' ? 'betriebserlaubnis' : 'belegung')
                  }
                  allowDeselect={false}
                  size="xs"
                  w={210}
                  aria-label={t('belegung:section.fachkraefte.bezug')}
                />
                {/* Die Betriebserlaubnis kennt keinen Jahresverlauf — ein
                    Monat waere dort gegenstandslos. */}
                {bedarfBasis === 'belegung' ? (
                  <Select
                    label={t('belegung:section.fachkraefte.zeitraum')}
                    data={[
                      { value: 'jahr', label: t('belegung:section.fachkraefte.ganzjaehrig') },
                      ...MONATE.map((m) => ({
                        value: String(m),
                        label: t(`common:month.long.${m}`),
                      })),
                    ]}
                    value={bedarfMonat == null ? 'jahr' : String(bedarfMonat)}
                    onChange={(v) =>
                      setBedarfMonat(v == null || v === 'jahr' ? null : Number(v))
                    }
                    allowDeselect={false}
                    size="xs"
                    w={150}
                    aria-label={t('belegung:section.fachkraefte.zeitraum')}
                  />
                ) : null}
              </Group>
            </Group>
            <Table withTableBorder withColumnBorders striped={false}>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td>{t('belegung:section.fachkraefte.soll')}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt(soll)}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>{t('belegung:section.fachkraefte.min')}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt(min)}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td>{t('belegung:section.fachkraefte.ist')}</Table.Td>
                  <Table.Td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fmt(ist)}
                  </Table.Td>
                </Table.Tr>
                <Table.Tr
                  style={{
                    backgroundColor:
                      diff < 0 ? 'var(--mantine-color-red-1)' : 'var(--mantine-color-teal-1)',
                  }}
                >
                  <Table.Td style={{ fontWeight: 700 }}>
                    {t('belegung:section.fachkraefte.differenz')}
                  </Table.Td>
                  <Table.Td
                    style={{
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      fontWeight: 700,
                      color:
                        diff < 0 ? 'var(--mantine-color-red-9)' : 'var(--mantine-color-teal-9)',
                    }}
                  >
                    {fmt(diff)}
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
        );
      })()}
    </Stack>
  );
}
